import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { useLocation } from 'react-router-dom';
import { Grid, Header } from 'semantic-ui-react';
import queryString from 'query-string';
import { format } from 'd3-format';
import { isEmpty, filter, propEq } from 'ramda';

import Query from 'scenes/Report/components/Query';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import { ClassProp } from 'helpers/types';
import Table from 'components/Table';
import { Field } from 'components/DownloadWidget/DownloadWidget';

import GET_MERCHANT_METRICS_BY_SEGMENT from './MerchantMetricsBySegment.query';
import styles from './MerchantMetricsBySegment.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;
type Props = {
  classes: Styles;
};

type MerchantMetricsBySegment = {
  cashbackPercentage: number;
  cashbackAmount: number;
  venueType: string;
  sales: number;
  transactions: number;
  aov: number;
  roas: number;
  totalCashbackAmount: number;
  totalCampaignSpend: number;
  minAov: number;
  maxAov: number;
};

type QueryResponse = {
  merchantMetricsBySegment: MerchantMetricsBySegment[];
};

const getTableHeader = (merchantName = '') => [
  {
    source: ['cashbackPercentage'],
    sortable: null,
    justify: 'left',
    label: 'Cashback Percentage',
    skipTotal: true,
  },
  {
    source: ['cashbackAmount'],
    sortable: null,
    justify: 'left',
    label: 'Cashback Amount',
    skipTotal: true,
  },
  {
    source: ['venueType'],
    sortable: ['venueType'],
    justify: 'left',
    label: 'Venue Type',
  },
  {
    source: ['transactions'],
    sortable: ['transactions'],
    justify: 'right',
    label: 'Total Transactions',
    transform: format(','),
  },
  {
    source: ['sales'],
    sortable: ['sales'],
    justify: 'right',
    label: 'Total Revenue',
    transform: format('$,.2f'),
  },
  {
    source: ['aov'],
    sortable: ['aov'],
    justify: 'right',
    label: 'AOV',
    transform: format('$,.0f'),
    calcMean: true,
  },
  {
    source: ['roas'],
    sortable: ['roas'],
    justify: 'right',
    label: 'ROAS',
    transform: format(',.0f'),
    calcMean: true,
  },
  {
    source: ['minAov'],
    sortable: ['minAov'],
    justify: 'right',
    label: 'Min AOV',
    transform: format('$,.0f'),
    calcMean: true,
  },
  {
    source: ['maxAov'],
    sortable: ['maxAov'],
    justify: 'right',
    label: 'MAX AOV',
    transform: format('$,.0f'),
    calcMean: true,
  },
  {
    source: ['totalCashbackAmount'],
    sortable: ['totalCashbackAmount'],
    justify: 'right',
    label: 'Total Cashback Amount',
    transform: format('$,.2f'),
  },
  {
    source: ['totalCampaignSpend'],
    sortable: ['totalCampaignSpend'],
    justify: 'right',
    label: 'Total Campaign Spend',
    transform: format('$,.2f'),
  },
];

const getCSVFields = (merchantName = '') => [
  {
    label: 'Cashback Amount',
    value: 'cashbackAmount',
  },
  {
    label: 'Venue Type',
    value: 'venueType',
  },
  {
    label: 'Total Transactions',
    value: ({ transactions }: MerchantMetricsBySegment) =>
      format(',')(transactions),
  },
  {
    label: 'Total Revenue',
    value: ({ sales }: MerchantMetricsBySegment) => format('$,.2f')(sales),
  },
  {
    label: 'AOV',
    value: ({ aov }: MerchantMetricsBySegment) => format(',.0f')(aov),
  },
  {
    label: 'ROAS',
    value: ({ roas }: MerchantMetricsBySegment) => format(',.0f')(roas),
  },
  {
    label: 'Min AOV',
    value: ({ minAov }: MerchantMetricsBySegment) => format(',.0f')(minAov),
  },
  {
    label: 'Max AOV',
    value: ({ maxAov }: MerchantMetricsBySegment) => format(',.0f')(maxAov),
  },
  {
    label: 'Total Memberships',
    value: 'totalMemberships',
  },
  {
    label: 'Total Revenue',
    value: ({ sales }: MerchantMetricsBySegment) => format('$,.2f')(sales),
  },
  {
    label: 'Cashback Amount',
    value: ({ totalCashbackAmount }: MerchantMetricsBySegment) =>
      format('$,.2f')(totalCashbackAmount),
  },
  {
    label: 'Total Campaign SPend',
    value: ({ totalCampaignSpend }: MerchantMetricsBySegment) =>
      format('$,.2f')(totalCampaignSpend),
  },
];

const renderTable = (
  tableHeader: any,
  data: MerchantMetricsBySegment[],
  CSVFields: Field[],
  classes: Styles,
  label: string,
) => (
  <>
    <Header className={classes.tableHeader} as="h4">
      Totals (in store and online) - {label}
    </Header>

    <Table
      sortDefaultColumn={{ name: 'Date', sortable: ['day'] }}
      tableHeader={tableHeader}
      data={data}
      splitEveryArg={25}
      showTotals
      exportData
      csvFields={CSVFields}
    />
  </>
);

const renderReport = (params: ReportParams, classes: Styles) => ({
  merchantMetricsBySegment,
}: QueryResponse) => {
  const tableHeader = getTableHeader(params.merchantName);
  const CSVFields = getCSVFields(params.merchantName);

  const cashback50 = filter(propEq('cashbackAmount', 0.5))(
    merchantMetricsBySegment,
  );
  const cashback46 = filter(propEq('cashbackAmount', 0.46))(
    merchantMetricsBySegment,
  );

  const cashback2percent = filter(propEq('cashbackPercentage', 2))(
    merchantMetricsBySegment,
  );

  const cashback5percent = filter(propEq('cashbackPercentage', 5))(
    merchantMetricsBySegment,
  );

  const cashback7percent = filter(propEq('cashbackPercentage', 7))(
    merchantMetricsBySegment,
  );

  return (
    <ReportBase header="Memberships Metrics" params={params}>
      <Grid.Row className={classes.charts} centered>
        <Grid.Column width={16} className={classes.gridTable}>
          <Header className={classes.tableHeader} as="h4">
            All totals include both cleared and pending transactions
          </Header>

          {!isEmpty(cashback50) &&
            renderTable(
              tableHeader,
              cashback50,
              CSVFields,
              classes,
              '$.50 Cashback Amount',
            )}

          {!isEmpty(cashback46) &&
            renderTable(
              tableHeader,
              cashback46,
              CSVFields,
              classes,
              '$.46 Cashback Amount',
            )}

          {!isEmpty(cashback2percent) &&
            renderTable(
              tableHeader,
              cashback2percent,
              CSVFields,
              classes,
              '2% Cashback',
            )}

          {!isEmpty(cashback5percent) &&
            renderTable(
              tableHeader,
              cashback5percent,
              CSVFields,
              classes,
              '5% Cashback',
            )}

          {!isEmpty(cashback7percent) &&
            renderTable(
              tableHeader,
              cashback7percent,
              CSVFields,
              classes,
              '7% Cashback',
            )}

          {!isEmpty(merchantMetricsBySegment) &&
            renderTable(
              tableHeader,
              merchantMetricsBySegment,
              CSVFields,
              classes,
              'Combined Cashback Amount Totals',
            )}
        </Grid.Column>
      </Grid.Row>
    </ReportBase>
  );
};

const AttMetricsBySegment: FunctionComponent<Props> = ({ classes }) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;

  return (
    <Query
      query={GET_MERCHANT_METRICS_BY_SEGMENT}
      reportName="ATT Metrics by Segment"
      queryKey="result"
      propName="attMetricsBySegment"
      checkForValidData={false}
      variables={{
        params,
      }}
      render={renderReport(params, classes)}
    />
  );
};

export default withStyles(styles as typeof styles)(AttMetricsBySegment);
